import { useState, useEffect } from 'react'
import styles from './EditMemberModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function EditMemberModal({ 
    isOpen, 
    onClose, 
    onSave,
    member,
    apiToken = null
}) {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        status: 'active',
        role: 'member'
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [fieldErrors, setFieldErrors] = useState({})

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !loading)

    // Initialize form data when modal opens or member changes
    useEffect(() => {
        if (isOpen && member) {
            setFormData({
                firstName: member.first_name || '',
                lastName: member.last_name || '',
                status: member.status || 'active',
                role: member.role || 'member'
            })
            setError('')
            setFieldErrors({})
        }
    }, [isOpen, member])

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
        
        // Clear field error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: ''
            }))
        }
    }

    const validateForm = () => {
        const errors = {}
        
        if (!formData.firstName.trim()) {
            errors.firstName = 'First name is required'
        }
        
        if (!formData.lastName.trim()) {
            errors.lastName = 'Last name is required'
        }
        
        if (!['active', 'inactive', 'pending'].includes(formData.status)) {
            errors.status = 'Invalid status selected'
        }
        
        if (!['member', 'admin'].includes(formData.role)) {
            errors.role = 'Invalid role selected'
        }
        
        setFieldErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }

        if (!member) {
            setError('Member information is missing')
            return
        }

        setLoading(true)
        setError('')

        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }

            apiClient.setAuthToken(apiToken)
            
            const updateData = {
                first_name: formData.firstName.trim(),
                last_name: formData.lastName.trim(),
                status: formData.status,
                role: formData.role
            }

            const response = await apiClient.updateWorkspaceMember(member.id, updateData)
            
            // Call onSave callback with updated member data
            onSave(response.member || { ...member, ...updateData })
            
            // Close modal
            handleClose()
            
        } catch (err) {
            console.error('Failed to update member:', err)
            setError(err.message || 'Failed to update member. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        if (!loading) {
            setFormData({
                firstName: '',
                lastName: '',
                status: 'active',
                role: 'member'
            })
            setError('')
            setFieldErrors({})
            onClose()
        }
    }

    const getStatusOptions = () => [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'pending', label: 'Pending' }
    ]

    const getRoleOptions = () => [
        { value: 'member', label: 'Member' },
        { value: 'admin', label: 'Admin' }
    ]

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Edit Member</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleClose}
                        disabled={loading}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={styles.modalBody}>
                        {member && (
                            <div className={styles.memberInfo}>
                                <span className={styles.memberLabel}>Editing:</span>
                                <span className={styles.memberName}>{member.full_name}</span>
                                <span className={styles.memberEmail}>({member.email})</span>
                            </div>
                        )}

                        {error && (
                            <div className={styles.errorMessage}>
                                {error}
                            </div>
                        )}

                        <div className={styles.formSection}>
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="firstName" className={styles.label}>
                                        First Name <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        className={`${styles.input} ${fieldErrors.firstName ? styles.inputError : ''}`}
                                        disabled={loading}
                                        placeholder="Enter first name"
                                    />
                                    {fieldErrors.firstName && (
                                        <div className={styles.fieldError}>{fieldErrors.firstName}</div>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="lastName" className={styles.label}>
                                        Last Name <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        className={`${styles.input} ${fieldErrors.lastName ? styles.inputError : ''}`}
                                        disabled={loading}
                                        placeholder="Enter last name"
                                    />
                                    {fieldErrors.lastName && (
                                        <div className={styles.fieldError}>{fieldErrors.lastName}</div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="status" className={styles.label}>
                                        Status <span className={styles.required}>*</span>
                                    </label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className={`${styles.select} ${fieldErrors.status ? styles.inputError : ''}`}
                                        disabled={loading}
                                    >
                                        {getStatusOptions().map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {fieldErrors.status && (
                                        <div className={styles.fieldError}>{fieldErrors.status}</div>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="role" className={styles.label}>
                                        Role <span className={styles.required}>*</span>
                                    </label>
                                    <select
                                        id="role"
                                        name="role"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                        className={`${styles.select} ${fieldErrors.role ? styles.inputError : ''}`}
                                        disabled={loading}
                                    >
                                        {getRoleOptions().map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {fieldErrors.role && (
                                        <div className={styles.fieldError}>{fieldErrors.role}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}